import type { Logger } from '@moojo/logger'
import type { StatsigClient } from '@statsig/js-client'
import mixpanel from 'mixpanel-browser'

export type EventTrackerContext = Partial<{
  brandName: string
  vfProjectId: string
  publisher: string
}>

/**
 * Interface for tracking analytics events with contextual information
 */
export interface EventTracker {
  /**
   * Track an analytics event with additional details
   * @param event - The name/type of event to track
   * @param details - Additional key-value details to include with the event
   */
  track(event: string, details?: Record<string, string>): void

  /**
   * Flush any pending events to ensure they are sent
   */
  flush(): Promise<void>

  /**
   * Update the shared context that will be included with all subsequent tracked events
   * @param context - The context object containing properties like brandName, projectId, etc
   */
  updateContext(context: EventTrackerContext): void
}

export class StatsigEventTracker implements EventTracker {
  private context: EventTrackerContext = {}

  constructor(private readonly client: StatsigClient) {}

  async flush(): Promise<void> {
    await this.client.flush()
  }

  track(event: string, details?: Record<string, string>): void {
    this.client.logEvent(event, undefined, { ...this.context, ...details })
  }

  updateContext(context: EventTrackerContext): void {
    this.context = { ...context }
  }
}

/**
 * Implementation of EventTracker that reports events to Mixpanel
 */
export class MixpanelEventTracker implements EventTracker {
  private context: EventTrackerContext = {}
  private isInitialized = false

  constructor(private readonly token: string) {}

  private ensureInitialized(): void {
    if (!this.isInitialized) {
      mixpanel.init(this.token, {
        debug: false,
        track_pageview: false,
        persistence: 'localStorage',
      })
      this.isInitialized = true
    }
  }

  async flush(): Promise<void> {
    // Mixpanel sends events immediately, no buffering to flush
  }

  track(event: string, details?: Record<string, string>): void {
    this.ensureInitialized()
    const eventProperties = {
      source: 'convo-agent',
      ...this.context,
      ...details,
    }
    mixpanel.track(event, eventProperties)
  }

  updateContext(context: EventTrackerContext): void {
    this.context = { ...context }
  }
}

/**
 * Implementation of EventTracker that reports to multiple trackers
 */
export class CompositeEventTracker implements EventTracker {
  constructor(private readonly trackers: EventTracker[]) {}

  async flush(): Promise<void> {
    await Promise.all(this.trackers.map(tracker => tracker.flush()))
  }

  track(event: string, details?: Record<string, string>): void {
    this.trackers.forEach(tracker => tracker.track(event, details))
  }

  updateContext(context: EventTrackerContext): void {
    this.trackers.forEach(tracker => tracker.updateContext(context))
  }
}

/**
 * Implementation of EventTracker that logs events to a Logger instance
 */
export class LoggerEventTracker implements EventTracker {
  private context: EventTrackerContext = {}

  constructor(private readonly logger: Logger) {}

  async flush(): Promise<void> {
    // Logger events are sent immediately, no buffering to flush
  }

  track(event: string, details?: Record<string, string>): void {
    this.logger.info(`Event tracked: ${event}`, { ...this.context, ...details })
  }

  updateContext(context: EventTrackerContext): void {
    this.logger.info('Event context updated:', context)
    this.context = { ...context }
  }
}
